import React from 'react';
import {
	BearingTypeCode,
	ClearanceCalculationPayload,
	ClearanceWidthDescriptor, TimkenBearingMaterial,
	TimkenBearingMounting,
	TimkenClearanceWidthDesignation,
	TimkenUnit,
} from '../../../components/bearing-search/types/types';
import { handleActionError } from '../../utils/engineering-tools';
import { BearingPartState } from '../../../components/bearing-search/reducers/reducer';
import { convertLength, convertNumberInput, isValidBoundary, isValidRange } from '../../utils/validations';
import { bearingClearanceImages, bearingMaterials } from '../../../components/bearing-search/api/api';

type ClearanceCalculationsValidationMap = Map<keyof ClearanceFormState, boolean>;

type ClearanceCalculationsNumberFields = Pick<
	ClearanceFormState,
	| 'shaftID'
	| 'housingOD'
	| 'shaftTemperature'
	| 'housingTemperature'
	| 'benchEndPlay'
	| 'spacerWidthTolerance'
	| 'designatedWidth'
	| 'minimumInnerFit'
	| 'maximumInnerFit'
	| 'maximumOuterFit'
	| 'minimumOuterFit'
>;

export type ClearanceFormState = {
	unit: TimkenUnit;
	shaftTemperature: number;
	housingTemperature: number;
	shaftID: number;
	housingOD: number;
	minimumOuterFit: number;
	maximumOuterFit: number;
	minimumInnerFit: number;
	maximumInnerFit: number;
	radialInternalClearance?: string;
	unmountedClearanceyMinimum?: number;
	unmountedClearanceyMaximum?: number;
	benchEndPlay?: number;
	spacerWidthTolerance?: number;
	bearingMounting: TimkenBearingMounting;
	designatedWidth?: number;
	designatedWidthDescriptor: ClearanceWidthDescriptor;
	designatedWidthImage?: string;
	bearingID: Record<TimkenUnit, number>;
	bearingOD: Record<TimkenUnit, number>;
	material: string;
	requestData?: Partial<ClearanceCalculationPayload>;
	validation: ClearanceCalculationsValidationMap;
	taperedBore: string;
};

export type ClearanceFormAction =
	| { type: 'change_unit'; payload: TimkenUnit; }
	| { type: 'change_number_field'; payload: [keyof ClearanceFormState, unknown]; }
	| { type: 'change_width_field', payload: number; }
	| { type: 'change_mounting'; payload: TimkenBearingMounting; }
	| { type: 'change_designated_width_descriptor'; payload: ClearanceWidthDescriptor; }
	| { type: 'change_radial_clearance'; payload: string; }
	| { type: 'update_request_data'; }
	| { type: 'reset'; payload: ClearanceFormState; }
	| { type: 'change_material'; payload: TimkenBearingMaterial; };

export const getInitialClearanceCalculationState = (
	bearing: BearingPartState,
	unit: TimkenUnit,
): ClearanceFormState => {
	const { bearingType, bearingSubType, bore, boreImperial, boreMetric, outerDiameterImperial, outerDiameterMetric, outerDiameter, taperedBore } = bearing;
	const bearingID = {
		M: boreMetric || Infinity,
		I: boreImperial || Infinity
	};
	const bearingOD = {
		M: outerDiameterMetric || 0,
		I: outerDiameterImperial || 0,
	};

	const requiredFields: Array<keyof ClearanceFormState> = [
		'shaftTemperature',
		'housingTemperature',
		'shaftID',
		'housingOD',
		'minimumOuterFit',
		'maximumOuterFit',
		'minimumInnerFit',
		'maximumInnerFit',
	];

	if ( bearingType === BearingTypeCode.TRB ) {
		requiredFields.push( 'benchEndPlay', 'spacerWidthTolerance' );

		if ( bearingSubType === 'TS' || bearingSubType === 'TSF' ) {
			requiredFields.push( 'designatedWidth' );
		}
	} else {
		requiredFields.push( 'unmountedClearanceyMinimum', 'unmountedClearanceyMaximum' );
	}

	const validation: ClearanceCalculationsValidationMap = new Map( requiredFields.map( ( key ) => [ key, true ] ) );
	const defaultValues = getDefaultValues( unit, bearingOD[ unit ] );
	const bearingMounting = TimkenBearingMounting.DIRECT;
	const designatedWidthDescriptor = 'coneBackFaceToConeBackFaceWidth';
	const designatedWidthImage = getDesignatedWidthImage( bearingMounting, designatedWidthDescriptor );

	return {
		...defaultValues,
		unit,
		bearingID,
		bearingOD,
		bearingMounting,
		designatedWidthDescriptor,
		designatedWidthImage,
		validation,
		taperedBore
	};
};

const getDefaultValues = ( unit: TimkenUnit, bearingOD: number ): ClearanceCalculationsNumberFields => {
	return {
		shaftTemperature: unit === 'M' ? 20 : 68,
		housingTemperature: unit === 'M' ? 20 : 68,
		shaftID: 0,
		housingOD: Number( ( bearingOD * 10 ).toFixed( 2 ) ),
		benchEndPlay: 0,
		spacerWidthTolerance: unit === 'M' ? 0.025 : 0.001,
		designatedWidth: 0,
		minimumOuterFit: 0,
		maximumOuterFit: 0,
		minimumInnerFit: 0,
		maximumInnerFit: 0,
	};
};

const getValidationMap = ( state: ClearanceFormState ): ClearanceCalculationsValidationMap => {
	const {
		unit,
		shaftID,
		housingOD,
		housingTemperature,
		shaftTemperature,
		unmountedClearanceyMinimum,
		unmountedClearanceyMaximum,
		benchEndPlay,
		spacerWidthTolerance,
		designatedWidth,
		minimumInnerFit,
		maximumInnerFit,
		minimumOuterFit,
		maximumOuterFit,
		bearingID,
		bearingOD,
	} = state;

	const validation = new Map( state.validation );

	validation.set( 'shaftID', isValidRange( shaftID, 0, bearingID[ unit ], false, true ) );

	validation.set( 'housingOD', isValidRange( housingOD, bearingOD[ unit ], Infinity, true ) );

	validation.set( 'housingTemperature', housingTemperature !== undefined );

	validation.set( 'shaftTemperature', shaftTemperature !== undefined );

	validation.set( 'minimumInnerFit', minimumInnerFit !== undefined && isValidBoundary( minimumInnerFit, maximumInnerFit ) );

	validation.set( 'maximumInnerFit', maximumInnerFit !== undefined && isValidBoundary( minimumInnerFit, maximumInnerFit ) );

	validation.set( 'minimumOuterFit', minimumOuterFit !== undefined && isValidBoundary( minimumOuterFit, maximumOuterFit ) );

	validation.set( 'maximumOuterFit', maximumOuterFit !== undefined && isValidBoundary( minimumOuterFit, maximumOuterFit ) );

	if ( validation.has( 'unmountedClearanceyMinimum' ) ) {
		validation.set(
			'unmountedClearanceyMinimum',
			isValidRange( unmountedClearanceyMinimum, 0, Infinity ) &&
			isValidBoundary( unmountedClearanceyMinimum, unmountedClearanceyMaximum ),
		);
		validation.set(
			'unmountedClearanceyMaximum',
			isValidRange( unmountedClearanceyMaximum, 0, Infinity ) &&
			isValidBoundary( unmountedClearanceyMinimum, unmountedClearanceyMaximum ),
		);
	}

	if ( validation.has( 'benchEndPlay' ) ) {
		validation.set( 'benchEndPlay', benchEndPlay !== undefined );
	}

	if ( validation.has( 'spacerWidthTolerance' ) ) {
		validation.set( 'spacerWidthTolerance', spacerWidthTolerance !== undefined );
	}

	if ( validation.has( 'designatedWidth' ) ) {
		validation.set( 'designatedWidth', isValidRange( designatedWidth, 0, Infinity ) );
	}

	return validation;
};

const getClearanceFormData = (
	state: ClearanceFormState,
): [ClearanceCalculationsValidationMap, Partial<ClearanceCalculationPayload> | undefined] => {
	const {
		unit,
		shaftID,
		housingOD,
		housingTemperature,
		shaftTemperature,
		unmountedClearanceyMinimum,
		unmountedClearanceyMaximum,
		benchEndPlay,
		spacerWidthTolerance,
		designatedWidth,
		minimumInnerFit,
		maximumInnerFit,
		minimumOuterFit,
		maximumOuterFit,
		bearingMounting,
		material,
	} = state;

	const {
		youngsModulus,
		youngsModulusMM,
		poissonRatio,
		thermalCoefficient: thermalCoefficientImperial,
		thermalCoefficientMetric,
	} = bearingMaterials.find( ( material ) => material.name === state.material ) || {};

	const designators = {
		'coneBackFaceToConeBackFaceWidth': 1,
		'coneFrontfacetoConeFrontfaceWidth': 2,
		'cupBackFaceToCupBackFaceWidth': 3,
		'cupFrontfacetoCupFrontfaceWidth': 4
	};

	const youngModulus = state.unit === 'M' ? youngsModulusMM : youngsModulus;
	const thermalCoefficient = state.unit === 'M' ? thermalCoefficientMetric : thermalCoefficientImperial;
	const validation = getValidationMap( state );
	const isFormValid = Array.from( validation.values() ).every( ( valid ) => valid );
	let requestData: Partial<ClearanceCalculationPayload> | undefined;

	if ( isFormValid ) {
		requestData = {
			unit,
			shaftID,
			housingOD,
			housingTemperature,
			shaftTemperature,
			unmountedClearanceyMinimum,
			unmountedClearanceyMaximum,
			benchEndPlay,
			spacerWidthTolerance,
			minimumInnerFit,
			maximumInnerFit,
			minimumOuterFit,
			maximumOuterFit,
			youngModulus,
			poissonRatio,
			thermalCoefficient,
			material
		};

		if ( validation.has( 'designatedWidth' ) ) {
			requestData[ state.designatedWidthDescriptor ] = designatedWidth;
			requestData[ 'widthDesignation' ] = designators[state.designatedWidthDescriptor];
			requestData.bearingMounting = bearingMounting;
			requestData.isBearingSpreadCalRequired = true;
		}
	}

	return [ validation, requestData ];
};

const getDesignatedWidthImage = ( mounting: TimkenBearingMounting, descriptor: ClearanceWidthDescriptor ): string => {
	const keyMap: Record<ClearanceWidthDescriptor, TimkenClearanceWidthDesignation> = {
		coneBackFaceToConeBackFaceWidth: 'cone_backface_to_cone_backface',
		coneFrontfacetoConeFrontfaceWidth: 'cone_frontface_to_cone_frontface',
		cupBackFaceToCupBackFaceWidth: 'cup_backface_to_cup_backface',
		cupFrontfacetoCupFrontfaceWidth: 'cup_frontface_to_cup_frontface',
	};

	const descriptorKey = keyMap[ descriptor ];
	const mountingKey = mounting === TimkenBearingMounting.DIRECT ? 'direct' : 'indirect';

	return bearingClearanceImages[ descriptorKey ][ mountingKey ];
};

const clearanceCalculationsReducer: React.Reducer<ClearanceFormState, ClearanceFormAction> = ( state, action ) => {
	switch ( action.type ) {
		case 'change_unit': {
			const unit = action.payload;
			const defaultValues = getDefaultValues( unit, state.bearingOD[ unit ] );

			const validation = new Map( state.validation );

			// Set all fields to valid since default values are filled
			for ( const key of validation.keys() ) {
				validation.set( key, true );
			}

			return {
				...state,
				...defaultValues,
				unit,
				validation,
			};
		}
		case 'change_number_field': {
			const [ key, value ] = action.payload;

			return {
				...state,
				[ key ]: convertNumberInput( value ),
			};
		}
		case 'change_width_field': {
			return {
				...state,
				designatedWidth: action.payload.pop()
			};
		}
		case 'change_mounting': {
			const bearingMounting = action.payload;
			const designatedWidthDescriptor = state.designatedWidthDescriptor;
			const designatedWidthImage = getDesignatedWidthImage( bearingMounting, designatedWidthDescriptor );

			return {
				...state,
				bearingMounting,
				designatedWidthImage,
			};
		}
		case 'change_designated_width_descriptor': {
			const designatedWidthDescriptor = action.payload;
			const bearingMounting = state.bearingMounting;
			const designatedWidthImage = getDesignatedWidthImage( bearingMounting, designatedWidthDescriptor );

			return {
				...state,
				designatedWidthDescriptor,
				designatedWidthImage,
			};
		}
		case 'change_radial_clearance': {
			let isDisabled = true;
			if (action.payload == 'Custom') {
				isDisabled =  false;
			}
			return {
				...state,
				radialInternalClearance: action.payload,
				isDisabled: isDisabled
			};
		}
		case 'update_request_data': {
			const [ validation, requestData ] = getClearanceFormData( state );

			return {
				...state,
				validation,
				requestData,
			};
		}

		case 'change_material': {
			const { name } =
				action.payload;

			return {
				...state,
				material: name || ''
			};
		}
		case 'reset': {
			return action.payload;
		}
	}

	return handleActionError( action );
};

export default clearanceCalculationsReducer;
